import { FormGroup, Input } from "reactstrap";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

import RecordSummarySettings from "components/Records/RecordSummarySettings";
import CustomToggle from "../CustomToggle";
import ReferenceModulePicker from "./ReferenceModulePicker";
import { modulesAtom } from "atoms/GlobalAtoms";

const FieldTypeReference = ({
  fieldValues,
  setFieldValues,
  showModificationProtection,
  moduleAPIName,
}) => {
  const modules = useAtomValue(modulesAtom);
  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    if (name === "source") {
      setFieldValues((prev) => ({
        ...prev,
        [name]: value,
        summary: false,
        summaryFields: {},
      }));
    }

    setFieldValues((prev) => ({ ...prev, [name]: value }));
  };

  const validModules = useMemo(() => {
    if (!Array.isArray(modules) || !modules.length) return [];

    return modules.filter((m) => !m?.Dynamic);
  }, [modules]);

  const handleSummaryCheck = (e) => {
    const { name, checked } = e.target;

    if (!checked) {
      return setFieldValues((prev) => ({
        ...prev,
        [name]: checked,
        summaryFields: {},
      }));
    }

    const summaryFields = {};

    if (Array.isArray(fieldValues.source)) {
      fieldValues.source.forEach((f) => {
        const moduleAPIName = f.replace("Tenant.Modules.", "");

        summaryFields[moduleAPIName] = { Fields: [] };
      });
    } else {
      const moduleAPIName = fieldValues.source.replace("Tenant.Modules.", "");

      summaryFields[moduleAPIName] = { Fields: [] };
    }

    setFieldValues((prev) => ({
      ...prev,
      [name]: checked,
      summaryFields: summaryFields,
    }));
  };

  const handleSummaryField = (fieldAPIName, dataSource) => {
    const allSummaryFields = JSON.parse(
      JSON.stringify(fieldValues.summaryFields)
    );
    const newSummaryFields = allSummaryFields[dataSource].Fields;

    // Handle subitem field deletion from summary fields list.
    const split = fieldAPIName?.split(".");

    if (split.length === 2) {
      fieldAPIName = split[1];
    }

    const foundIndex = newSummaryFields.indexOf(fieldAPIName);

    // Remove field from summary fields if it exists, otherwise add it
    if (foundIndex > -1) {
      newSummaryFields.splice(foundIndex, 1);
    } else {
      newSummaryFields.push(fieldAPIName);
    }

    setFieldValues((prev) => ({ ...prev, summaryFields: allSummaryFields }));
  };

  const handleReorderSummaryFields = (newOrder, dataSource) => {
    const newSumFields = { ...fieldValues.summaryFields };
    const newFields = newOrder;

    if (!newFields) return;

    newSumFields[dataSource].Fields = [...newFields];

    setFieldValues((prev) => ({
      ...prev,
      summaryFields: { ...newSumFields },
    }));
  };

  return (
    <>
      <FormGroup>
        <h4 className="mb-0">Selectable Field Options</h4>
        <p>
          <small>
            Select the name of the source for reference options to be offered by
            this field.
          </small>
        </p>
        <ReferenceModulePicker
          fieldValues={fieldValues}
          setFieldValues={setFieldValues}
        />
      </FormGroup>
      <CustomToggle
        title="Record Summary"
        description="Display a summary of the referenced record"
        stateName="summary"
        value={fieldValues.source === "Any" ? false : fieldValues.summary}
        checked={fieldValues.summary}
        handleChange={handleSummaryCheck}
        disabled={
          (fieldValues.protected && !showModificationProtection) ||
          !fieldValues.source ||
          fieldValues.source === "Any"
        }
        containerCSS="mb-3"
      />
      {fieldValues.summary &&
        fieldValues.source.length > 0 &&
        fieldValues.source !== "Any" && (
          <RecordSummarySettings
            handleReorderFields={handleReorderSummaryFields}
            handleSelectField={handleSummaryField}
            dataSources={
              Array.isArray(fieldValues.source)
                ? fieldValues.source
                : [fieldValues.source]
            }
            summaryFields={fieldValues.summaryFields}
            fieldType={fieldValues.fieldType}
            setFieldValues={setFieldValues}
            displayModeChange={(value, dataSource) => {
              const summary = {
                ...fieldValues?.summaryFields[dataSource],
              };
              summary.summaryPresentation = value;
              setFieldValues((prev) => {
                prev.summaryFields[dataSource] = { ...summary };
                return {
                  ...prev,
                };
              });
            }}
          />
        )}
      {((Array.isArray(fieldValues?.source) &&
        fieldValues?.source.length > 1) ||
        fieldValues.source === "Any") && (
        <FormGroup className="mt-3">
          <h4 className="mb-1">Default Source</h4>
          <div className="small mb-3">
            The default source that will be used when selecting a reference.
          </div>
          <Input
            type="select"
            name="defaultSource"
            value={fieldValues.defaultSource}
            onChange={handleSelectChange}
            disabled={fieldValues.protected && !showModificationProtection}
          >
            <option disabled value="">
              Select an option
            </option>
            <optgroup label="CRM Modules">
              {validModules.map((mod, i) => (
                <option key={i} value={`Tenant.Modules.${mod.APIName}`}>
                  {mod.Name}
                </option>
              ))}
            </optgroup>
          </Input>
        </FormGroup>
      )}
      {(fieldValues?.source?.includes("Tenant.Modules.Contacts") ||
        fieldValues.source === "Any") && (
        <FormGroup className="mt-3">
          <CustomToggle
            title="Show Cloud Users Only"
            description="For Contact references, only show Contacts that have a user directly
          associated with them"
            stateName="GcUsers"
            value={fieldValues.gcUsers}
            checked={fieldValues.gcUsers}
            handleChange={() =>
              setFieldValues((prev) => ({ ...prev, gcUsers: !prev.gcUsers }))
            }
            containerCSS="mb-3"
          />
        </FormGroup>
      )}
      {fieldValues?.fieldType === "SingleReference" &&
        (fieldValues?.source?.includes(`Tenant.Modules.${moduleAPIName}`) ||
          fieldValues.source === "Any") && (
          <FormGroup className="mt-3">
            <CustomToggle
              title="Hierarchical"
              stateName="HierarchicalToggle"
              description="Parent records can't be referenced by the selected referenced records in the parent one."
              value={fieldValues?.hierarchical}
              checked={fieldValues?.hierarchical}
              handleChange={() =>
                setFieldValues((prev) => ({
                  ...prev,
                  hierarchical: !prev.hierarchical,
                }))
              }
              containerCSS="mb-3"
            />
          </FormGroup>
        )}
    </>
  );
};

export default FieldTypeReference;
